import { tns } from 'tiny-slider/src/tiny-slider';
import BaseComponent from 'foundation/base-component';

const LAYOUT_HORIZONTAL = 'horizontal';
const LAYOUT_VERTICAL = 'vertical';
const THEME_DARK = 'dark';
const THEME_LIGHT = 'light';
const AUTOPLAY_TIMEOUT_BASE = 4000;
const AUTOPLAY_TIMEOUT_STEP = 750;

const verticalResponsiveOptions = {
    0: {
        items: 1.5,
        gutter: 20,
    },
    560: {
        items: 2,
        gutter: 20,
    },
    850: {
        items: 3,
        gutter: 20,
    },
    1024: {
        items: 4,
        gutter: 20,
    },
    1200: {
        items: 5,
        gutter: 20,
        slideBy: 2,
    },
};

const horizontalResponsiveOptions = {
    0: {
        items: 1.5,
        gutter: 20,
    },
    560: {
        items: 2,
        gutter: 20,
    },
    850: {
        items: 3,
        gutter: 20,
    },
    1024: {
        items: 2,
        gutter: 20,
    },
    1200: {
        items: 2.5,
        gutter: 20,
        slideBy: 2,
    },
};

class FeaturesCarouselComponent extends BaseComponent {
    constructor(...args) {
        super(...args);

        this.carousel = null;
        this.layout = this.getLayout();
        this.responsiveOptions = this.getResponsiveOptions();
        this.slideCount = this.getSlideCount();
        this.handleResize = this.handleResize.bind(this);
        this.resizeFrame = null;
        this.theme = this.getTheme();

        this.init();
    }

    static getRequiredRefs() {
        return ['slides'];
    }

    init() {
        this.toggleCarousel();
        window.addEventListener('resize', this.handleResize);
    }

    handleResize() {
        if (this.resizeFrame) {
            return;
        }

        this.resizeFrame = window.requestAnimationFrame(() => {
            this.resizeFrame = null;
            this.toggleCarousel();
        });
    }

    toggleCarousel() {
        const shouldEnable = this.shouldEnableCarousel();

        if (shouldEnable && !this.carousel) {
            this.carousel = tns({
                container: this.refs.slides,
                slideBy: this.isHorizontalLayout() ? 1 : 'page',
                gutter: 10,
                loop: false,
                rewind: false,
                nav: true,
                navPosition: 'bottom',
                controls: false,
                autoplay: false,
                autoplayTimeout: this.getAutoplayTimeout(),
                autoplayButtonOutput: false,
                freezable: false,
                touch: true,
                mouseDrag: true,
                swipeAngle: false,
                responsive: this.responsiveOptions,
            });
        }

        if (!shouldEnable && this.carousel) {
            this.carousel.destroy();
            this.carousel = null;
        }
    }

    shouldEnableCarousel() {
        return this.slideCount > this.getVisibleItems();
    }

    getLayout() {
        const layout =
            this.element && this.element.dataset && this.element.dataset.layout ?
                this.element.dataset.layout.trim().toLowerCase() :
                '';

        return layout === LAYOUT_HORIZONTAL || layout === LAYOUT_VERTICAL ?
            layout :
            '';
    }

    getTheme() {
        const theme =
            this.element && this.element.dataset && this.element.dataset.theme ?
                this.element.dataset.theme.trim().toLowerCase() :
                '';

        return theme === THEME_DARK || theme === THEME_LIGHT ? theme : '';
    }

    isHorizontalLayout() {
        return this.layout === LAYOUT_HORIZONTAL;
    }

    getResponsiveOptions() {
        return this.isHorizontalLayout() ?
            horizontalResponsiveOptions :
            verticalResponsiveOptions;
    }

    getSlideCount() {
        return this.refs.slides ? this.refs.slides.children.length : 0;
    }

    getInstanceIndex() {
        if (!this.element) {
            return 0;
        }

        const instances = [].slice.call(
            document.querySelectorAll(
                '[data-dc-component="features-carousel"]',
            ),
        );
        const index = instances.indexOf(this.element);

        return index >= 0 ? index : 0;
    }

    getAutoplayTimeout() {
        return (
            AUTOPLAY_TIMEOUT_BASE +
            this.getInstanceIndex() * AUTOPLAY_TIMEOUT_STEP
        );
    }

    getVisibleItems() {
        return Object.keys(this.responsiveOptions)
            .map(Number)
            .sort((a, b) => a - b)
            .reduce((items, breakpoint) => {
                if (window.innerWidth >= breakpoint) {
                    return this.responsiveOptions[breakpoint].items;
                }

                return items;
            }, 1);
    }

    onDestroy() {
        window.removeEventListener('resize', this.handleResize);

        if (this.resizeFrame) {
            window.cancelAnimationFrame(this.resizeFrame);
            this.resizeFrame = null;
        }

        if (this.carousel) {
            this.carousel.destroy();
            this.carousel = null;
        }
    }
}

export default FeaturesCarouselComponent;
